import { Trans, useTranslation } from 'react-i18next';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { BenefitUsageType } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { LearningCentreMethod } from '@/web/components/LearningCentreMethod/LearningCentreMethod';
import { LearningCentreModal } from '@/web/components/LearningCentreModal/LearningCentreModal';
import {
  Flex,
  IconCoverflexApp,
  IconCreditCard,
  IconSeedling,
  TextAction,
} from '@coverflex-tech/hypernova';

export const ESWalletBenefitsLearningCentre = ({
  onClose,
  availableMethods,
}: TESWalletBenefitsLearningCentreProps) => {
  const { t } = useTranslation();
  const { company } = useEmployeeDetails();

  const benefitsPolicy = company.documents.find(
    (document) => document.slug === 'benefits-policy'
  );

  return (
    <LearningCentreModal
      icon={<IconSeedling />}
      title={t('learning_centre.es.wallet.benefits.title')}
      subtitle={
        benefitsPolicy ? (
          <Trans
            i18nKey="learning_centre.es.wallet.benefits.subtitle_with_link"
            components={{
              policyLink: (
                <TextAction
                  href={benefitsPolicy.url}
                  download={benefitsPolicy.name ?? true}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    AnalyticsService.trackCustomEvent(
                      'benefits_policy_download_button_clicked',
                      { context: 'benefits_page' }
                    );
                  }}
                >
                  link
                </TextAction>
              ),
            }}
          />
        ) : (
          <Trans i18nKey="learning_centre.es.wallet.benefits.subtitle" />
        )
      }
      onClose={onClose}
      steps={[
        <Flex gap="2xl" key={0}>
          {availableMethods.includes('app') && (
            <LearningCentreMethod
              icon={<IconCoverflexApp />}
              title={t(
                'learning_centre.es.wallet.benefits.methods.coverflex_app.title'
              )}
            >
              <Trans i18nKey="learning_centre.es.wallet.benefits.methods.coverflex_app.description" />
            </LearningCentreMethod>
          )}
          {availableMethods.includes('card') && (
            <LearningCentreMethod
              icon={<IconCreditCard />}
              title={t('learning_centre.es.wallet.benefits.methods.card.title')}
            >
              <Trans i18nKey="learning_centre.es.wallet.benefits.methods.card.description" />
            </LearningCentreMethod>
          )}
        </Flex>,
      ]}
    />
  );
};

export type TESWalletBenefitsLearningCentreProps = {
  onClose: () => void;
  availableMethods: Extract<BenefitUsageType, 'app' | 'card'>[];
};
